import React from 'react';

import { Alert, AlertTitle, Box, Button, Divider, Typography } from '@mui/material';
import { FormApi } from 'final-form';

import { Field, Form } from '@hrlogics/react-components/form';

import { SSO_EMAIL_CHANGE_BLOCKED_MESSAGE } from '@clearverify/constants';

import {
  CheckIfUserEmailChangeExistDocument,
  useCheckIfUserEmailChangeExistQuery,
  useCreateUserEmailChangeRequestMutation,
} from 'src/api';
import { FullscreenLoader } from 'src/components/common';
import { TextField } from 'src/components/finalForm';
import { ModalId } from 'src/constants/modals';
import { useAccountPersonalInformation } from 'src/hooks/account';
import { useNotification } from 'src/hooks/app';
import { useCurrentUserAuthStatus } from 'src/hooks/auth';
import { useScreenInfo } from 'src/hooks/common';
import { useModal } from 'src/hooks/shared';
import { useCurrentUser, useCurrentUserPermissions } from 'src/hooks/user';
import { UserEmailChangeRequestConfirmModal } from 'src/modals/userEmailChangeRequest';
import { SxProp } from 'src/types/mui';

import { ChangeEmailFormFields, ChangeEmailFormValues } from './types';
import { changeEmailFormValidationSchema } from './utils';

export const ChangeEmailForm: React.FC = () => {
  const { isMobile } = useScreenInfo();

  const { createApolloContext } = useNotification();

  const [createUserEmailChangeRequestMutation] = useCreateUserEmailChangeRequestMutation({
    context: createApolloContext({
      successMessage: 'Change Email Request Created Successfully. Check Your Email!',
    }),
  });

  const { openModal } = useModal();

  const { user: currentUser, isUserLoading } = useCurrentUser();
  const { hasVerifierRoles, isEmployee } = useCurrentUserPermissions();

  const { data: changeRequestAlreadyExistsData, loading: checkIfRequestExistsLoading } =
    useCheckIfUserEmailChangeExistQuery();

  const changeRequestAlreadyExists =
    changeRequestAlreadyExistsData?.checkIfUserEmailChangeExist.success;

  const { accountPersonalInformation, loading: personalInformationLoading } =
    useAccountPersonalInformation(currentUser?.id);

  const { isSsoEnabled, loading: ssoStatusLoading } = useCurrentUserAuthStatus();

  const initialValues: Partial<ChangeEmailFormValues> = React.useMemo(
    () => ({
      [ChangeEmailFormFields.email]: accountPersonalInformation?.email || '',
      [ChangeEmailFormFields.newEmail]: '',
    }),
    [accountPersonalInformation],
  );
  const loading = isUserLoading || personalInformationLoading || ssoStatusLoading;

  const onSubmit = React.useCallback(
    (data: ChangeEmailFormValues, form: FormApi<ChangeEmailFormValues>) => {
      openModal(ModalId.EmailChangeConfirm, {
        onSubmit: async () => {
          if (!data.newEmail) return;
          try {
            await createUserEmailChangeRequestMutation({
              variables: {
                newEmail: data.newEmail,
              },
              refetchQueries: [
                {
                  query: CheckIfUserEmailChangeExistDocument,
                },
              ],
            });

            form.restart();
          } catch (error) {
            console.error(error);
          }
        },
      });
    },
    [createUserEmailChangeRequestMutation, openModal],
  );

  if (loading || checkIfRequestExistsLoading) {
    return <FullscreenLoader />;
  }

  return (
    <Box>
      {!!isEmployee && (
        <Alert variant="outlined" severity="info">
          <AlertTitle>Note</AlertTitle>
          We recommend to use your personal Email address to save access to your account.
        </Alert>
      )}

      <Form
        onSubmit={onSubmit}
        initialValues={initialValues}
        containerProps={{ sx: formStyles, p: hasVerifierRoles && isMobile ? 3 : 0 }}
        subscription={{ dirty: true, valid: true, submitting: true }}
        validationSchema={changeEmailFormValidationSchema}
      >
        {({ dirty, valid, submitting }) => (
          <React.Fragment>
            {!!changeRequestAlreadyExists && (
              <Box>
                <Alert variant="outlined" icon={false} severity="info">
                  Change Email Request already exists. Check Your old and new Email addresses for
                  confirmation letters or enter new email to restart process
                </Alert>
              </Box>
            )}

            {!!isSsoEnabled && (
              <Box>
                <Alert variant="outlined" severity="info">
                  {SSO_EMAIL_CHANGE_BLOCKED_MESSAGE}
                </Alert>
              </Box>
            )}

            <Box sx={formHeaderStyles}>
              <Box>
                <Typography variant="subtitle1">Email Address</Typography>
                <Typography
                  component="p"
                  variant="h3"
                  fontSize="16px"
                  lineHeight="24px"
                  color="grey"
                >
                  You can change your login email. To do that you need to confirm the action from
                  both old and new email.
                </Typography>
              </Box>

              <Box>
                <Button
                  disabled={
                    !dirty || !valid || submitting || changeRequestAlreadyExists || isSsoEnabled
                  }
                  loading={submitting}
                  variant={isMobile ? 'outlined' : 'contained'}
                  sx={{ whiteSpace: 'nowrap' }}
                  type="submit"
                >
                  Change Email
                </Button>
              </Box>
            </Box>

            <Divider />

            <Box display="flex" flexDirection="column" gap={2}>
              <Field
                name={ChangeEmailFormFields.email}
                fullWidth
                label="Current Email"
                required
                component={TextField}
                disabled
              />
              <Field
                name={ChangeEmailFormFields.newEmail}
                fullWidth
                label="New Email"
                required
                component={TextField}
                disabled={isSsoEnabled}
              />
            </Box>
          </React.Fragment>
        )}
      </Form>
      <UserEmailChangeRequestConfirmModal />
    </Box>
  );
};

const formStyles: SxProp = theme => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  mt: 3,
  padding: 3,
  border: 1,
  borderRadius: 2,
  borderColor: theme.palette.border?.light,
});

const formHeaderStyles: SxProp = theme => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 2,
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
});
